/**
 * The T2 Business report's `sdp` view — column definitions.
 *
 * This grid cannot use the shared `groupedColumns` formatter, and the reason is structural rather
 * than cosmetic: the KPI matrix puts four different kinds of number in the same twelve columns.
 * `Order Qty MPO`, `SDP on time qty` and `Outstanding qty` are quantities; `SDP %` is a ratio. The
 * shared formatter is handed a value and a column, never the row, so it would render 0.9514 as "1".
 *
 * The formatter therefore keys off `kpi_type` on the row. That is presentation choosing a number
 * format, not the page deciding a value — every figure here is computed in SQL (§4.3).
 */

import type { ColumnDef } from '@tanstack/react-table';

import { decimal, integer } from '@/components/report/groupedColumns';
import type { ReportColumn } from '@/lib/api';

import { RankFilter } from './sdp-rank-filter';

/** The KPI whose cells are ratios rather than counts. Matches the literal in sdp.sql. */
export const RATIO_KPI = 'SDP %';

const MONTH_FIELDS = new Set([
  'm01', 'm02', 'm03', 'm04', 'm05', 'm06', 'm07', 'm08', 'm09', 'm10', 'm11', 'm12',
]);

/**
 * A plain result row. An alias rather than an interface with named fields: the page holds every
 * view's rows in one shape, and a narrower type here would not be assignable to it.
 */
export type SdpRow = Record<string, unknown>;

export function formatSdpCell(value: unknown, row: SdpRow): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) {
    return String(value);
  }
  // A month with nothing delivered yet returns null from NULLIF, not zero — already handled above.
  return row.kpi_type === RATIO_KPI ? `${decimal.format(n * 100)}%` : integer.format(n);
}

/** Declared so the query can order the KPI rows within a block; not a figure a reader asked for. */
const ORDERING_ONLY = new Set(['kpi_seq']);

/** The field the server ranks on, and the one this grid filters. */
export const RANK_FIELD = 'supplier_rank';

/** The supplier identity shared by the four KPI rows in a block. */
const SUPPLIER_FIELD = 't2_group';

function supplierCell(value: unknown): React.ReactNode {
  const text = value === null || value === undefined || value === '' ? '—' : String(value);
  return <span className="block max-w-40 whitespace-normal break-words">{text}</span>;
}

/**
 * How many suppliers the grid shows. `null` is every one of them - the default, because a report
 * that opens already hiding rows makes a reader argue with a number that is not on screen.
 */
export type TopSuppliers = number | null;

/**
 * @param topN   how many suppliers are on screen, or null for all
 * @param onTopN null when the result was cut short - the Rank header then carries no filter,
 *               because a top-N of a partial result is a claim about a set nobody has
 */
export function sdpColumns(
  columns: readonly ReportColumn[],
  topN: TopSuppliers = null,
  onTopN: ((next: TopSuppliers) => void) | null = null,
): ColumnDef<SdpRow>[] {
  return columns
    // kpi_seq orders the KPI rows within a supplier block; readers never see it.
    .filter((column) => !ORDERING_ONLY.has(column.field))
    .map((column) => {
      const isRank = column.field === RANK_FIELD;
      const isSupplier = column.field === SUPPLIER_FIELD;
      const numeric = MONTH_FIELDS.has(column.field) || column.field === 'qty_total' || isRank;
      return {
        id: column.field,
        accessorKey: column.field,
        header:
          isRank && onTopN !== null
            ? () => <RankFilter value={topN} onChange={onTopN} />
            : column.label,
        enableSorting: column.sortable === true,
        size: isRank ? 56 : numeric ? 72 : 140,
        minSize: 44,
        meta: {
          exportLabel: column.label,
          // One supplier, four KPI rows: the rank and the name are the block's, not each row's.
          // Merged down the block rather than repeated four times - the four rows are one
          // supplier's answer, and the merge is what says where that answer ends.
          spanRows: isSupplier || isRank,
          align: numeric ? ('right' as const) : ('left' as const),
          cellClassName: numeric ? 'px-1 font-mono tabular-nums' : undefined,
          headerClassName: numeric ? 'px-1' : undefined,
        },
        cell: ({ row }) =>
          isRank
            // A position, not one of the four KPIs: never formatted as a percentage.
            ? ((row.original[column.field] as number | undefined) ?? '—')
            : isSupplier
              ? supplierCell(row.original[column.field])
            : numeric
              ? formatSdpCell(row.original[column.field], row.original)
              : ((row.original[column.field] as string | undefined) ?? '—'),
      } satisfies ColumnDef<SdpRow>;
    });
}
